const INVOICE_REGEX = /^[A-Za-z0-9]+$/;
export const INVOICE_BILL_NO_MAX_LEN = 32;
export const ASSET_NAME_PATTERN = /^[a-zA-Z0-9]+(?: [a-zA-Z0-9]+)*$/;
/** tbl_properties_assets.purchase_value DECIMAL(12,2) — up to 10 digits before decimal, 2 after */
const PURCHASE_VALUE_REGEX = /^\d+(\.\d{1,2})?$/;
export const PURCHASE_VALUE_MAX = 9999999999.99;
export const PURCHASE_VALUE_INTEGER_DIGITS_MAX = 10;

const parseDateAtStartOfDay = (dateString) => {
  if (!dateString) return null;
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
};

export const validateDateNotFuture = (dateString) => {
  if (!dateString) return "Required";
  const inputDate = parseDateAtStartOfDay(dateString);
  if (!inputDate) return "Enter a valid date";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (inputDate > today) return "Date cannot be in the future";
  return "";
};

export const validatePropertyAssetDateRelations = (formData) => {
  const errors = {};
  const purchase = parseDateAtStartOfDay(formData.purchase_date);
  const warranty = parseDateAtStartOfDay(formData.warranty_expiry_date);
  const physical = parseDateAtStartOfDay(formData.physical_verification_date);

  if (purchase && warranty && warranty.getTime() <= purchase.getTime()) {
    errors.warranty_expiry_date =
      "Warranty expiry date must be after purchase date and cannot be the same date";
  }

  if (purchase && physical && physical.getTime() < purchase.getTime()) {
    errors.physical_verification_date =
      "Physical verification date cannot be before purchase date";
  }

  if (physical && warranty && physical.getTime() >= warranty.getTime()) {
    errors.physical_verification_date =
      "Physical verification date must be before warranty expiry date and cannot be the same date";
  }

  return errors;
};

export const validateRequiredText = (value, label, maxLen = 200) => {
  const v = String(value ?? "").trim();
  if (!v) return `${label} is required`;
  if (v.length > maxLen) return `${label} must be at most ${maxLen} characters`;
  return "";
};

export const validateAssetName = (value) => {
  const v = String(value ?? "").trim();
  if (!v) return "Property / asset name is required";
  if (v.length > 200) return "Property / asset name must be at most 200 characters";
  if (!ASSET_NAME_PATTERN.test(v)) {
    return "Letters and numbers only; special characters are not allowed";
  }
  return "";
};

export const validatePurchaseValue = (value) => {
  const v = String(value ?? "").trim();
  if (!v) return "Purchase value is required";
  if (!PURCHASE_VALUE_REGEX.test(v)) {
    return "Use numbers only (up to 2 decimal places)";
  }
  const [whole] = v.split(".");
  if (whole.length > PURCHASE_VALUE_INTEGER_DIGITS_MAX) {
    return `Purchase value is too large (maximum ${PURCHASE_VALUE_INTEGER_DIGITS_MAX} digits before decimal)`;
  }
  const n = Number(v);
  if (!Number.isFinite(n) || n < 1) return "Purchase value must be at least 1";
  if (n > PURCHASE_VALUE_MAX) {
    return `Purchase value is too large (maximum ${PURCHASE_VALUE_MAX.toLocaleString("en-IN")})`;
  }
  return "";
};

export const validatePositiveInt = (value, label, min = 1) => {
  const v = String(value ?? "").trim();
  if (!v) return `${label} is required`;
  if (!/^\d+$/.test(v)) return "Use numbers only";
  const n = Number(v);
  if (!Number.isInteger(n) || n < min) {
    return `${label} must be at least ${min}`;
  }
  return "";
};

export const validatePositiveNumber = (value, label, min = 1) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n < min) {
    return `${label} must be at least ${min}`;
  }
  return "";
};

export const validateInvoiceBillNo = (value) => {
  const v = String(value ?? "").trim();
  if (!v) return "Invoice / bill number is required";
  if (!INVOICE_REGEX.test(v)) {
    return "Use letters and numbers only (no special characters)";
  }
  if (v.length > INVOICE_BILL_NO_MAX_LEN) {
    return `Must be at most ${INVOICE_BILL_NO_MAX_LEN} characters`;
  }
  return "";
};

export const validatePropertyAssetForm = (formData, { isEdit = false, requireImage = true, mode = "full" } = {}) => {
  const errors = {};

  if (mode === "subAdmin") {
    const nameErr = validateAssetName(formData.property_asset_name);
    if (nameErr) errors.property_asset_name = nameErr;
    const locErr = validateRequiredText(formData.location_room, "Location / room", 200);
    if (locErr) errors.location_room = locErr;
    if (!formData.condition_id) errors.condition_id = "Condition is required";
    const brandErr = validateRequiredText(formData.brand_model, "Brand / model", 200);
    if (brandErr) errors.brand_model = brandErr;
    const remErr = validateRequiredText(formData.remarks, "Remarks", 200);
    if (remErr) errors.remarks = remErr;
    return errors;
  }

  const nameErr = validateAssetName(formData.property_asset_name);
  if (nameErr) errors.property_asset_name = nameErr;

  if (!formData.category_id) errors.category_id = "Category is required";
  if (!formData.condition_id) errors.condition_id = "Condition is required";
  if (!formData.property_status_id) errors.property_status_id = "Asset status is required";
  if (!formData.responsible_person_id) errors.responsible_person_id = "Responsible person is required";

  const qtyErr = validatePositiveInt(formData.quantity, "Quantity", 1);
  if (qtyErr) errors.quantity = qtyErr;

  const valErr = validatePurchaseValue(formData.purchase_value);
  if (valErr) errors.purchase_value = valErr;

  const purchaseDateErr = validateDateNotFuture(formData.purchase_date);
  if (purchaseDateErr) errors.purchase_date = purchaseDateErr;

  const physErr = validateDateNotFuture(formData.physical_verification_date);
  if (physErr) errors.physical_verification_date = physErr;

  if (!formData.warranty_expiry_date) {
    errors.warranty_expiry_date = "Warranty expiry date is required";
  } else if (Number.isNaN(new Date(formData.warranty_expiry_date).getTime())) {
    errors.warranty_expiry_date = "Enter a valid date";
  }

  const relationErrors = validatePropertyAssetDateRelations(formData);
  for (const [key, message] of Object.entries(relationErrors)) {
    if (!errors[key]) errors[key] = message;
  }

  const fields = [
    ["brand_model", "Brand / model"],
    ["supplier_vendor", "Supplier / vendor"],
    ["location_room", "Location / room"],
    ["remarks", "Remarks"],
  ];
  for (const [key, label] of fields) {
    const err = validateRequiredText(formData[key], label, 200);
    if (err) errors[key] = err;
  }

  const invoiceErr = validateInvoiceBillNo(formData.invoice_bill_no);
  if (invoiceErr) errors.invoice_bill_no = invoiceErr;

  if (requireImage && !formData._hasNewImage) {
    errors.asset_image_url = "Asset image is required";
  }

  return errors;
};
